import { useMemo, useState } from 'react';
import { AsyncBoundary, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useRectifications, useDecideRectification,
  type RectificationStatus, type RectificationSubjectKind,
  type RectificationRow,
} from '@/lib/queries';

/**
 * /tpo/rectification (Page 82/N) — DPDP §14 correction request review.
 *
 * Students submit "please correct this field on my record" requests.
 * TPO / principal / registrar / HoD review and approve, reject, or
 * ask for more info. Approvals become an audit-logged data change.
 */

const SUBJECT_LABEL: Record<RectificationSubjectKind, string> = {
  StudentProfile: 'Student profile',
  Application: 'Application',
  Offer: 'Offer',
  Badge: 'Badge',
  Membership: 'Membership',
  Interview: 'Interview',
  FacultyProfile: 'Faculty profile',
  Other: 'Other',
};

const STATUS_LABEL: Record<RectificationStatus, string> = {
  pending: 'Pending',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  more_info_needed: 'Awaiting info',
  withdrawn: 'Withdrawn',
};

function statusTone(s: RectificationStatus): string {
  if (s === 'pending' || s === 'in_review') return 'badge-warning';
  if (s === 'approved') return 'badge-success';
  if (s === 'rejected') return 'badge-danger';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function daysLeft(iso?: string): { text: string; breached: boolean } {
  if (!iso) return { text: '—', breached: false };
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { text: '—', breached: false };
  const days = Math.ceil((then - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return { text: `${-days}d overdue`, breached: true };
  if (days === 0) return { text: 'due today', breached: false };
  return { text: `${days}d left`, breached: false };
}

function shorten(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

type Decision = 'approve' | 'reject' | 'more_info';

export function TpoRectificationPage() {
  const toast = useToast();
  const [status, setStatus] = useState<RectificationStatus | ''>('pending');
  const [subjectKind, setSubjectKind] = useState<RectificationSubjectKind | ''>('');
  const [slaOnly, setSlaOnly] = useState(false);
  const [q, setQ] = useState('');

  const list = useRectifications({ status, subjectKind, slaBreached: slaOnly || undefined, q, sortBy: 'slaDeadline', sortDir: 'asc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary ?? {};

  const [openRow, setOpenRow] = useState<RectificationRow | null>(null);
  const [decision, setDecision] = useState<Decision>('approve');
  const [note, setNote] = useState('');
  const decide = useDecideRectification(openRow?._id ?? '');
  const canSubmit = useMemo(() => Boolean(openRow) && note.trim().length >= 5, [openRow, note]);

  const closeDrawer = () => { setOpenRow(null); setNote(''); setDecision('approve'); };

  const submit = () => {
    if (!openRow) return;
    decide.mutate(
      { decision, reviewNote: note.trim() },
      {
        onSuccess: () => {
          const verb = decision === 'approve' ? 'Approved' : decision === 'reject' ? 'Rejected' : 'Marked need for more info';
          toast.show({ message: `${verb}.` });
          closeDrawer();
        },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">DPDP · §14</p>
          <h1 className="h-page">Rectification requests</h1>
          <p className="subhead">
            Students asking to correct wrong data on their record. Approve when the
            proposed value is supported by evidence; reject with a written reason;
            ask for more info when unsure.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">Pending</span>
          <span className="kpi-value">{summary.pending ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">In review</span>
          <span className="kpi-value">{summary.in_review ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Approved</span>
          <span className="kpi-value">{summary.approved ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Rejected</span>
          <span className="kpi-value">{summary.rejected ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search field path or justification" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as RectificationStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[RectificationStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={subjectKind} onChange={(e) => setSubjectKind(e.target.value as RectificationSubjectKind | '')}>
            <option value="">Any subject</option>
            {(Object.entries(SUBJECT_LABEL) as Array<[RectificationSubjectKind, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={slaOnly} onChange={(e) => setSlaOnly(e.target.checked)} />
            <span>SLA breached only</span>
          </label>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No requests in scope"
          emptyHint="Widen the filters, or wait for the next request — students file these when they spot bad data."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Filed</th>
                  <th>Student</th>
                  <th>Subject · field</th>
                  <th>Change</th>
                  <th>SLA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sla = daysLeft(r.slaDeadline);
                  return (
                    <tr key={r._id} onClick={() => setOpenRow(r)} style={{ cursor: 'pointer' }}>
                      <td className="cell-sub">{fmt(r.createdAt)}</td>
                      <td>
                        <div className="cell-title">
                          {typeof r.user === 'object' && r.user?.fullName ? r.user.fullName : '—'}
                        </div>
                        {typeof r.user === 'object' && r.user?.email && (
                          <div className="cell-sub">{r.user.email}</div>
                        )}
                      </td>
                      <td>
                        <div className="cell-title">{SUBJECT_LABEL[r.subject.kind]}</div>
                        <div className="cell-sub">{r.fieldPath}</div>
                      </td>
                      <td className="cell-sub">
                        <span title={String(r.currentValue ?? '')}>{shorten(r.currentValue)}</span>
                        {' → '}
                        <span title={String(r.proposedValue ?? '')}>{shorten(r.proposedValue)}</span>
                      </td>
                      <td className={sla.breached ? 'cell-danger' : 'cell-sub'}>{sla.text}</td>
                      <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Approving is a data change.</b> Approval writes to the underlying record and
        stamps an audit log entry. Reject with a reason when the proposed value isn't
        supported by evidence; ask for more info when you're not sure.
      </Notice>

      <Drawer open={Boolean(openRow)} onClose={closeDrawer} title={openRow?.fieldPath ?? 'Rectification'} size="lg">
        {openRow && (
          <div className="stack">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`badge ${statusTone(openRow.status)}`}>{STATUS_LABEL[openRow.status]}</span>
                <span className="cell-sub">{SUBJECT_LABEL[openRow.subject.kind]}</span>
              </div>
              <p className="cell-sub m-0 mt-1">
                Filed {fmt(openRow.createdAt)}
                {typeof openRow.user === 'object' && openRow.user?.fullName && ` by ${openRow.user.fullName}`}
              </p>
              <p className="cell-sub m-0">SLA deadline: {fmt(openRow.slaDeadline)} · {daysLeft(openRow.slaDeadline).text}</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="card card-pad">
                <div className="eyebrow">Current value</div>
                <pre className="m-0 mt-1 text-sm whitespace-pre-wrap break-words">
                  {openRow.currentValue === null || openRow.currentValue === undefined
                    ? '—' : typeof openRow.currentValue === 'string' ? openRow.currentValue : JSON.stringify(openRow.currentValue, null, 2)}
                </pre>
              </div>
              <div className="card card-pad">
                <div className="eyebrow">Proposed value</div>
                <pre className="m-0 mt-1 text-sm whitespace-pre-wrap break-words">
                  {typeof openRow.proposedValue === 'string' ? openRow.proposedValue : JSON.stringify(openRow.proposedValue, null, 2)}
                </pre>
              </div>
            </div>

            <div className="card card-pad">
              <div className="eyebrow">Justification</div>
              <p className="m-0 mt-1 whitespace-pre-line">{openRow.justification}</p>
            </div>

            {openRow.evidenceUrls && openRow.evidenceUrls.length > 0 && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Evidence</div>
                <ul className="grid gap-1 text-sm">
                  {openRow.evidenceUrls.map((u) => (
                    <li key={u}><a className="link" href={u} target="_blank" rel="noreferrer">{u}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {openRow.reviewNote && (
              <div className="card card-pad">
                <div className="eyebrow">Prior reviewer note</div>
                <p className="m-0 mt-1 whitespace-pre-line">{openRow.reviewNote}</p>
              </div>
            )}

            {(openRow.status === 'pending' || openRow.status === 'in_review' || openRow.status === 'more_info_needed') && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Decide</div>
                <div className="grid gap-2 grid-cols-3">
                  {(['approve', 'reject', 'more_info'] as Decision[]).map((d) => (
                    <label key={d} className={`card card-pad cursor-pointer ${decision === d ? 'border-primary' : ''}`}>
                      <input type="radio" name="decision" value={d} checked={decision === d} onChange={() => setDecision(d)} className="mr-2" />
                      <b>{d === 'approve' ? 'Approve' : d === 'reject' ? 'Reject' : 'Need more info'}</b>
                    </label>
                  ))}
                </div>
                <label className="block mt-3">
                  <span className="eyebrow">Review note (5-2000 chars)</span>
                  <textarea
                    className="input mt-1"
                    rows={4}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What was the evidence, or what more do you need?"
                  />
                </label>
                <div className="flex justify-end gap-2 mt-3">
                  <button className="btn btn-ghost" onClick={closeDrawer}>Cancel</button>
                  <button className="btn btn-primary" disabled={!canSubmit || decide.isPending} onClick={submit}>
                    {decide.isPending ? 'Saving…' : 'Save decision'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
